window.getWakaBodyScrollLock =
  window.getWakaBodyScrollLock ||
  function getWakaBodyScrollLock() {
    if (window.wakaBodyScrollLock) return window.wakaBodyScrollLock;

    let lockCount = 0;
    let previousOverflow = '';
    let previousPaddingRight = '';

    window.wakaBodyScrollLock = {
      lock() {
        lockCount += 1;
        if (lockCount > 1) return;

        const body = document.body;
        previousOverflow = body.style.overflow;
        previousPaddingRight = body.style.paddingRight;

        const scrollbarWidth =
          window.innerWidth - document.documentElement.clientWidth;
        if (scrollbarWidth > 0) {
          const computedPaddingRight =
            parseFloat(window.getComputedStyle(body).paddingRight) || 0;
          body.style.paddingRight = `${computedPaddingRight + scrollbarWidth}px`;
        }

        body.style.overflow = 'hidden';
      },

      unlock() {
        if (lockCount === 0) return;
        lockCount -= 1;
        if (lockCount > 0) return;

        const body = document.body;
        body.style.overflow = previousOverflow;
        body.style.paddingRight = previousPaddingRight;
      },
    };

    return window.wakaBodyScrollLock;
  };

/**
 * Header heights resolve from CSS custom properties on <body>, which only
 * change when header.js re-measures. Reading them through getComputedStyle on
 * every call forced a style recalc on per-frame scroll paths, so the resolved
 * values are cached and invalidated explicitly instead.
 */
window.getWakaHeaderMetrics =
  window.getWakaHeaderMetrics ||
  (() => {
    let cache = null;

    const readVariable = (style, name) => {
      const parsed = Number.parseFloat(style.getPropertyValue(name));
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const compute = () => {
      const style = getComputedStyle(document.body);
      const width = window.innerWidth;
      let breakpoint;

      if (width >= 1024) {
        breakpoint = 'desktop';
      } else if (width >= 768) {
        breakpoint = 'tablet';
      } else {
        breakpoint = 'mobile';
      }

      return {
        header: readVariable(style, `--header-height-${breakpoint}`),
        headerMain: readVariable(style, `--header-main-height-${breakpoint}`),
      };
    };

    const getMetrics = () => {
      if (!cache) {
        cache = compute();
      }
      return cache;
    };

    getMetrics.invalidate = () => {
      cache = null;
    };

    window.addEventListener('resize', getMetrics.invalidate, { passive: true });

    return getMetrics;
  })();

// Call after anything writes the --header-*-height custom properties.
window.invalidateWakaHeaderMetrics =
  window.invalidateWakaHeaderMetrics ||
  function invalidateWakaHeaderMetrics() {
    window.getWakaHeaderMetrics.invalidate();
  };

window.getHeaderHeight =
  window.getHeaderHeight ||
  function getHeaderHeight() {
    return window.getWakaHeaderMetrics().header;
  };

window.getHeaderMainHeight =
  window.getHeaderMainHeight ||
  function getHeaderMainHeight() {
    return window.getWakaHeaderMetrics().headerMain;
  };

/**
 * Defers expensive setup until an element approaches the viewport.
 * Fires immediately when IntersectionObserver is unavailable.
 * Returns a cleanup function callers should invoke on disconnect.
 */
window.wakaWhenVisible =
  window.wakaWhenVisible ||
  function wakaWhenVisible(element, callback, options = {}) {
    if (!('IntersectionObserver' in window)) {
      callback();
      return () => {};
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        callback();
      },
      { rootMargin: options.rootMargin || '300px' },
    );

    observer.observe(element);

    return () => observer.disconnect();
  };

/**
 * Container inline padding used to align slider edges with the grid.
 * Resolved once per page rather than once per slider instance.
 */
window.getWakaSliderOffsets =
  window.getWakaSliderOffsets ||
  (() => {
    let cache = null;

    const compute = () => {
      const rootStyles = getComputedStyle(document.documentElement);
      const read = (varName, fallback) => {
        const value = parseFloat(rootStyles.getPropertyValue(varName).trim());
        return Number.isFinite(value) ? value : fallback;
      };

      return {
        mobile: read('--container-padding-inline', 16),
        lg: read('--container-padding-inline-lg', 40),
        xl: read('--container-padding-inline-xl', 72),
      };
    };

    const getOffsets = () => {
      if (!cache) {
        cache = compute();
      }
      return cache;
    };

    window.addEventListener(
      'resize',
      () => {
        cache = null;
      },
      { passive: true },
    );

    return getOffsets;
  })();

window.wakaCookie =
  window.wakaCookie ||
  (function getWakaCookie() {
    const DEFAULT_PATH = '/';
    const DEFAULT_SAME_SITE = 'Lax';

    const buildBaseCookie = (name, value) => {
      return `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
    };

    const buildOptions = (days, options = {}) => {
      const parts = [];
      const path = options.path || DEFAULT_PATH;
      const sameSite = options.sameSite || DEFAULT_SAME_SITE;

      parts.push(`path=${path}`);
      parts.push(`SameSite=${sameSite}`);

      if (typeof days === 'number' && Number.isFinite(days)) {
        const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
        parts.push(`expires=${expires.toUTCString()}`);
      }

      if (options.secure === true || window.location.protocol === 'https:') {
        parts.push('Secure');
      }

      return parts.join('; ');
    };

    return {
      set(name, value, days = 30, options = {}) {
        const safeValue = value == null ? '' : String(value);
        const base = buildBaseCookie(name, safeValue);
        const optionString = buildOptions(days, options);
        document.cookie = `${base}; ${optionString}`;
      },

      get(name) {
        const encodedName = `${encodeURIComponent(name)}=`;
        const cookieParts = document.cookie.split(';');

        for (const cookiePart of cookieParts) {
          const part = cookiePart.trim();
          if (part.startsWith(encodedName)) {
            return decodeURIComponent(part.slice(encodedName.length));
          }
        }

        return null;
      },

      remove(name, options = {}) {
        this.set(name, '', -1, options);
      },
    };
  })();

(() => {
  const MORPH_EVENT = 'waka:morph:done';
  const CONTENT_UPDATED_EVENT = 'waka:content-updated';

  const dispatchMorphEvents = (detail = {}) => {
    const root = detail.root || document;
    const skipIfVisible = detail.skipIfVisible === true;

    document.dispatchEvent(
      new CustomEvent(MORPH_EVENT, {
        detail: {
          ...detail,
          root,
          skipIfVisible,
        },
      }),
    );

    document.dispatchEvent(
      new CustomEvent(CONTENT_UPDATED_EVENT, {
        detail: {
          root,
          skipIfVisible,
        },
      }),
    );
  };

  window.wakaMorph = (destination, source, options = {}, eventOptions = {}) => {
    if (typeof Idiomorph === 'undefined' || !destination || !source) {
      return null;
    }

    Idiomorph.morph(destination, source, options);

    const root =
      typeof eventOptions.resolveRoot === 'function'
        ? eventOptions.resolveRoot()
        : eventOptions.root || destination;

    dispatchMorphEvents({
      context: eventOptions.context,
      destination,
      root,
      skipIfVisible: eventOptions.skipIfVisible,
      source,
    });

    return root;
  };
})();

(() => {
  const targetSelector = '.waka-fade';
  const visibleClass = 'is-visible';
  const skipTransitionClass = 'waka-fade--skip-transition';

  const getFadeConfig = () => {
    const body = document.body;
    const enabled = body?.dataset.fadeAnimationEnabled !== 'false';

    const parsedThreshold = Number.parseFloat(
      body?.dataset.fadeRevealThreshold || '0.25',
    );
    const revealThreshold = Number.isFinite(parsedThreshold)
      ? Math.min(Math.max(parsedThreshold, 0), 1)
      : 0.25;

    const revealRootMargin =
      body?.dataset.fadeRevealRootMargin?.trim() || '0px 0px -10% 0px';

    return {
      enabled,
      revealThreshold,
      revealRootMargin,
    };
  };

  const reveal = (element) => {
    element.classList.add(visibleClass);
  };

  // Elements revealed in the same tick share a single rAF to drop the
  // skip-transition class, rather than scheduling one frame callback each.
  let pendingSkipTransition = [];
  let skipTransitionFrame = null;

  const revealWithoutTransition = (element) => {
    element.classList.add(skipTransitionClass);
    reveal(element);

    pendingSkipTransition.push(element);
    if (skipTransitionFrame !== null) return;

    skipTransitionFrame = requestAnimationFrame(() => {
      skipTransitionFrame = null;
      const batch = pendingSkipTransition;
      pendingSkipTransition = [];
      batch.forEach((el) => el.classList.remove(skipTransitionClass));
    });
  };

  const isInViewport = (element) => {
    const rect = element.getBoundingClientRect();
    return (
      rect.bottom > 0 &&
      rect.right > 0 &&
      rect.top < window.innerHeight &&
      rect.left < window.innerWidth
    );
  };

  const getFadeElements = (root = document) => {
    if (!(root instanceof Element)) {
      return Array.from(document.querySelectorAll(targetSelector));
    }

    const elements = [];

    if (root.matches(targetSelector)) {
      elements.push(root);
    }

    elements.push(...root.querySelectorAll(targetSelector));

    return elements;
  };

  const createObserver = (revealThreshold, revealRootMargin) => {
    if (!('IntersectionObserver' in window)) {
      return null;
    }

    return new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          reveal(entry.target);
          observer.unobserve(entry.target);
        });
      },
      {
        root: null,
        rootMargin: revealRootMargin,
        threshold: revealThreshold,
      },
    );
  };

  const initializeFadeObserver = () => {
    const fadeConfig = getFadeConfig();

    if (!fadeConfig.enabled) {
      document.querySelectorAll(targetSelector).forEach((element) => {
        reveal(element);
      });
      return;
    }

    const observer = createObserver(
      fadeConfig.revealThreshold,
      fadeConfig.revealRootMargin,
    );

    const observeTargets = (root = document, options = {}) => {
      const { skipIfVisible = false } = options;

      if (root instanceof Element && !root.isConnected) {
        return;
      }

      const elements = getFadeElements(root).filter(
        (element) => !element.classList.contains(visibleClass),
      );

      if (!elements.length) {
        return;
      }

      if (!observer) {
        elements.forEach(reveal);
        return;
      }

      if (!skipIfVisible) {
        elements.forEach((element) => observer.observe(element));
        return;
      }

      // Reading a rect on an element inside a content-visibility:auto section
      // forces the browser to lay out that section's skipped subtree. Section
      // wrapper boxes are always laid out, though — so test the section first:
      // an element inside a fully off-screen section cannot be in the viewport,
      // and no rect inside it ever needs reading. Only elements in sections
      // that intersect the viewport (already rendered anyway) get measured.
      const offscreenBySection = new Map();
      const inOffscreenSection = (element) => {
        const section = element.closest('[id^="shopify-section-"]');
        if (!section) return false;

        let offscreen = offscreenBySection.get(section);
        if (offscreen === undefined) {
          const rect = section.getBoundingClientRect();
          offscreen =
            rect.bottom <= 0 ||
            rect.right <= 0 ||
            rect.top >= window.innerHeight ||
            rect.left >= window.innerWidth;
          offscreenBySection.set(section, offscreen);
        }

        return offscreen;
      };

      // Measure everything before writing a single class. Interleaving the
      // two made each iteration invalidate layout for the next rect read.
      const visibility = elements.map(
        (element) => !inOffscreenSection(element) && isInViewport(element),
      );

      elements.forEach((element, index) => {
        if (visibility[index]) {
          revealWithoutTransition(element);
          return;
        }

        observer.observe(element);
      });
    };

    // Every pass after the initial one is triggered by content landing in the
    // DOM (fetches, morphs, app scripts). Queue those and drain once per
    // animation frame: the first rect read then happens right before the
    // browser's own layout for that frame instead of forcing a mid-task one,
    // and passes triggered in the same frame collapse into a single flush.
    let pendingPasses = [];
    let pendingFlush = null;

    const queuePass = (root, options = {}) => {
      pendingPasses.push([root, options]);
      if (pendingFlush !== null) return;

      pendingFlush = requestAnimationFrame(() => {
        pendingFlush = null;
        const batch = pendingPasses;
        pendingPasses = [];
        batch.forEach(([batchRoot, batchOptions]) => {
          observeTargets(batchRoot, batchOptions);
        });
      });
    };

    const refreshFade = (root = document, options = {}) => {
      queuePass(root, options);
    };

    window.refreshWakaFade = refreshFade;

    // The initial pass stays synchronous: theme.js is deferred, so this runs
    // before first paint and above-fold content is revealed in that paint.
    observeTargets(document, { skipIfVisible: true });

    document.addEventListener('shopify:section:load', (event) => {
      queuePass(event.target || document);
    });

    document.addEventListener('waka:content-updated', (event) => {
      const root = event.detail?.root;
      const skipIfVisible = event.detail?.skipIfVisible === true;
      queuePass(root || document, { skipIfVisible });
    });

    // Third-party app scripts insert nodes constantly; each added element
    // rides the same per-frame queue.
    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) {
            return;
          }

          queuePass(node);
        });
      });
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeFadeObserver, {
      once: true,
    });
  } else {
    initializeFadeObserver();
  }
})();

(() => {
  const TRUNCATE_SELECTOR = '[data-text-truncate]';
  const TOGGLE_ATTR = 'data-text-truncate-toggle';
  const TYPOGRAPHY_CLASSES = [
    'body-xs',
    'body-sm',
    'body-base',
    'body-lg',
    'body-xl',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
  ];

  const store = new WeakMap();

  const getTypographyClass = (el) => {
    return TYPOGRAPHY_CLASSES.find((className) =>
      el.classList.contains(className),
    );
  };

  // All three helpers below take an already-resolved CSSStyleDeclaration so a
  // single getComputedStyle() call covers the whole collapse pass instead of
  // three separate style recalcs per element.
  const getLineHeight = (computed) => {
    const parsedLineHeight = Number.parseFloat(computed.lineHeight);

    if (Number.isFinite(parsedLineHeight)) {
      return parsedLineHeight;
    }

    const parsedFontSize = Number.parseFloat(computed.fontSize);
    return Number.isFinite(parsedFontSize) ? parsedFontSize * 1.4 : 20;
  };

  const getTruncationLines = (el, computed) => {
    const inlineValue = Number.parseInt(
      el.style.getPropertyValue('--truncation-lines'),
      10,
    );

    if (Number.isFinite(inlineValue) && inlineValue > 0) {
      return inlineValue;
    }

    const computedValue = Number.parseInt(
      computed.getPropertyValue('--truncation-lines'),
      10,
    );

    if (Number.isFinite(computedValue) && computedValue > 0) {
      return computedValue;
    }

    return 3;
  };

  // The block ships with `.text-truncate` (CSS line-clamp) applied server-side,
  // so an element whose content already fits needs no toggle and no measuring.
  const isOverflowing = (el) => el.scrollHeight > el.clientHeight + 1;

  const createToggle = (el, label) => {
    const button = document.createElement('button');
    button.setAttribute('type', 'button');
    button.setAttribute(TOGGLE_ATTR, '');

    const typographyClass = getTypographyClass(el);
    const typography = typographyClass ? `${typographyClass} ` : '';

    button.className = `${typography}text-primary underline bg-transparent border-none p-0 cursor-pointer align-baseline`;
    button.textContent = label;

    return button;
  };

  const createMeasureElement = (el) => {
    const measure = el.cloneNode(false);
    measure.removeAttribute('data-text-truncate');
    measure.removeAttribute('data-show-more-label');
    measure.removeAttribute('data-show-less-label');
    measure.removeAttribute(TOGGLE_ATTR);

    measure.style.position = 'fixed';
    measure.style.visibility = 'hidden';
    measure.style.pointerEvents = 'none';
    measure.style.left = '-9999px';
    measure.style.top = '0';
    measure.style.zIndex = '-1';
    // The binary search below re-reads scrollHeight after every textContent
    // write. Containment scopes each of those re-layouts to this node instead
    // of the whole document, which is what made the search expensive.
    measure.style.contain = 'layout style';
    measure.style.height = 'auto';
    measure.style.maxHeight = 'none';
    measure.style.overflow = 'visible';
    // Copy clamp properties from the real element so measurement matches CSS.
    const clamp = el.style.getPropertyValue('--truncation-lines') || '3';
    measure.style.webkitLineClamp = clamp;
    measure.style.lineClamp = clamp;
    measure.style.display = '-webkit-box';
    measure.style.webkitBoxOrient = 'vertical';
    measure.style.display = 'block';
    measure.style.width = `${Math.max(el.clientWidth, 1)}px`;

    return measure;
  };

  const getCollapsedLength = (el, fullText, showMoreLabel, maxHeight) => {
    const measure = createMeasureElement(el);
    document.body.appendChild(measure);

    try {
      const fullToggle = createToggle(el, showMoreLabel);
      measure.textContent = `${fullText}... `;
      measure.appendChild(fullToggle);

      if (measure.scrollHeight <= maxHeight + 0.5) {
        return fullText.length;
      }

      let low = 0;
      let high = fullText.length;
      let best = 0;

      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        const candidate = fullText.slice(0, mid).replace(/\s+$/, '');
        const toggle = createToggle(el, showMoreLabel);

        measure.textContent = `${candidate}... `;
        measure.appendChild(toggle);

        if (measure.scrollHeight <= maxHeight + 0.5) {
          best = mid;
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }

      return best;
    } finally {
      measure.remove();
    }
  };

  const setExpanded = (el, state) => {
    const showLessLabel = el.dataset.showLessLabel || 'Show less';
    const showMoreLabel = el.dataset.showMoreLabel || 'Show more';

    if (state.expanded) {
      el.classList.remove('text-truncate');
      el.innerHTML = state.originalHtml;
      const button = createToggle(el, showLessLabel);
      button.addEventListener('click', () => {
        state.expanded = false;
        setCollapsed(el, state);
      });
      el.insertAdjacentText('beforeend', ' ');
      el.appendChild(button);
      return;
    }

    // No geometry means the element is display:none or sits in a subtree the
    // browser has not rendered. Measuring there yields a 1px-wide clone and a
    // nonsense cut point, so leave the CSS clamp to handle it on its own.
    if (el.clientHeight === 0) {
      return;
    }

    // Fast path: the CSS clamp already fits the copy, so there is nothing to
    // truncate and no toggle to add. This one read replaces a cloned node plus
    // a binary search worth of forced layouts for every short text block.
    if (!isOverflowing(el)) {
      return;
    }

    // Only measure in a hidden clone, never show full text in the live element.
    const fullText = state.originalText;
    const elStyle = window.getComputedStyle(el);
    const lines = getTruncationLines(el, elStyle);
    const paddingTop = Number.parseFloat(elStyle.paddingTop) || 0;
    const paddingBottom = Number.parseFloat(elStyle.paddingBottom) || 0;
    const maxHeight =
      getLineHeight(elStyle) * lines + paddingTop + paddingBottom;

    const collapsedLength = getCollapsedLength(
      el,
      fullText,
      showMoreLabel,
      maxHeight,
    );

    // If no truncation is needed, leave the clamped HTML as-is (do not swap in full text).
    if (collapsedLength >= fullText.length) {
      // Do nothing: let the original clamped HTML remain.
      return;
    }

    // Otherwise, update the element with the truncated text and toggle.
    const button = createToggle(el, showMoreLabel);
    button.addEventListener('click', () => {
      state.expanded = true;
      setExpanded(el, state);
    });
    const truncated = fullText.slice(0, collapsedLength).replace(/\s+$/, '');
    el.textContent = `${truncated}... `;
    el.appendChild(button);
  };

  const setCollapsed = (el, state) => {
    state.expanded = false;
    el.classList.add('text-truncate');
    setExpanded(el, state);
  };

  const setupTruncation = (el) => {
    el.querySelector(`[${TOGGLE_ATTR}]`)?.remove();

    store.set(el, {
      originalHtml: el.innerHTML,
      originalText: (el.textContent || '').replace(/\s+/g, ' ').trim(),
      expanded: false,
    });

    const state = store.get(el);
    setCollapsed(el, state);
  };

  // Collapsing costs a cloned node plus a binary search of forced layouts, so
  // hold it off until the element is near the viewport instead of paying for
  // every text block on the page at DOMContentLoaded.
  // Run measurement in idle time rather than inline in the observer callback.
  // Landing in a scroll frame made every forced layout compete with the
  // browser's own work, which cost more than measuring everything up front.
  const runWhenIdle =
    typeof window.requestIdleCallback === 'function'
      ? (task) => window.requestIdleCallback(task, { timeout: 500 })
      : (task) => setTimeout(task, 0);

  const lazyObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries, observer) => {
            const targets = [];

            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              observer.unobserve(entry.target);
              targets.push(entry.target);
            });

            if (!targets.length) return;

            runWhenIdle(() => {
              targets.forEach((target) => {
                if (target.isConnected) {
                  setupTruncation(target);
                }
              });
            });
          },
          { rootMargin: '200px' },
        )
      : null;

  const scheduleTruncation = (el) => {
    if (!lazyObserver) {
      setupTruncation(el);
      return;
    }

    lazyObserver.unobserve(el);
    lazyObserver.observe(el);
  };

  const initTruncation = (root = document) => {
    const elements = [];

    if (root instanceof Element) {
      if (root.hasAttribute('data-text-truncate')) {
        elements.push(root);
      }
      elements.push(...root.querySelectorAll(TRUNCATE_SELECTOR));
    } else {
      elements.push(...root.querySelectorAll(TRUNCATE_SELECTOR));
    }

    elements.forEach(scheduleTruncation);
  };

  const init = () => {
    initTruncation(document);

    document.addEventListener('shopify:section:load', (event) => {
      initTruncation(event.target);
    });

    document.addEventListener('shopify:block:load', (event) => {
      initTruncation(event.target);
    });

    document.addEventListener('waka:morph:done', (event) => {
      initTruncation(event.detail?.root || document);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

(() => {
  const scrollToHash = (hash) => {
    if (!hash) return;

    const id = hash.startsWith('#') ? hash.slice(1) : hash;
    if (!id) return;

    const target =
      document.getElementById(id) ||
      document.querySelector(`[name="${CSS.escape(id)}"]`);
    if (!target) return;

    const headerHeight = window.getHeaderHeight();
    const additionalOffset = window.innerWidth >= 1024 ? 30 : 20;
    const top =
      target.getBoundingClientRect().top +
      window.scrollY -
      (headerHeight + additionalOffset);
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const onHashLinkClick = (event) => {
    const link = event.target.closest('a[href*="#"]');
    if (!link) return;

    const url = new URL(link.href, window.location.href);

    // Only handle same-page hash links
    if (
      url.pathname !== window.location.pathname ||
      url.hostname !== window.location.hostname
    )
      return;
    if (!url.hash) return;

    event.preventDefault();
    history.pushState(null, '', url.hash);
    scrollToHash(url.hash);
  };

  const init = () => {
    document.addEventListener('click', onHashLinkClick);

    if (window.location.hash) {
      scrollToHash(window.location.hash);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

(() => {
  const FIXED_BELOW_HEADER_SELECTOR = '.fixed-below-header';
  const placeholders = new WeakMap();

  // Takes the caller's already-measured height so no read happens between the
  // style writes below.
  const ensurePlaceholder = (element, elementHeight) => {
    let placeholder = placeholders.get(element);
    if (placeholder && placeholder.isConnected) {
      placeholder.style.height = `${elementHeight}px`;
      return placeholder;
    }

    placeholder = document.createElement('div');
    placeholder.style.width = '100%';
    placeholder.style.height = `${elementHeight}px`;
    placeholder.style.display = 'none';
    element.insertAdjacentElement('afterend', placeholder);
    placeholders.set(element, placeholder);
    return placeholder;
  };

  const setFixedState = (element, isFixed, headerHeight) => {
    // Read first, then write — reading offsetHeight after touching the
    // placeholder's styles forced a synchronous layout on every call.
    const elementHeight = element.offsetHeight;
    const placeholder = ensurePlaceholder(element, elementHeight);

    if (isFixed) {
      placeholder.style.display = '';

      element.classList.add('is-fixed');
      element.style.position = 'fixed';
      element.style.top = `${headerHeight}px`;
      element.style.left = '0';
      element.style.right = '0';
      element.style.width = '100%';
      element.style.marginLeft = '0';
      element.dataset.fixedBelowHeaderActive = 'true';
      return;
    }

    element.classList.remove('is-fixed');
    placeholder.style.display = 'none';
    element.style.position = '';
    element.style.top = '';
    element.style.left = '';
    element.style.right = '';
    element.style.width = '';
    element.style.marginLeft = '';
    delete element.dataset.fixedBelowHeaderActive;
  };

  // Measures where an element sits without changing anything.
  const measureElement = (element) => {
    const isActive = element.dataset.fixedBelowHeaderActive === 'true';
    const placeholder = placeholders.get(element);
    const source = isActive && placeholder ? placeholder : element;

    return {
      element,
      isActive: isActive && !!placeholder,
      top: source.getBoundingClientRect().top,
    };
  };

  const applyMeasurement = (measurement, headerHeight) => {
    const { element, isActive, top } = measurement;

    if (isActive) {
      if (top > headerHeight) {
        setFixedState(element, false, headerHeight);
        return;
      }

      element.style.top = `${headerHeight}px`;
      element.style.left = '0';
      element.style.right = '0';
      element.style.width = '100%';
      element.style.marginLeft = '0';
      return;
    }

    if (top <= headerHeight) {
      setFixedState(element, true, headerHeight);
    }
  };

  const applyFixedBelowHeader = (root = document) => {
    const elements = [];

    if (root instanceof Element) {
      if (root.matches(FIXED_BELOW_HEADER_SELECTOR)) {
        elements.push(root);
      }
      elements.push(...root.querySelectorAll(FIXED_BELOW_HEADER_SELECTOR));
    } else {
      elements.push(...document.querySelectorAll(FIXED_BELOW_HEADER_SELECTOR));
    }

    // Bail before resolving header heights. This runs on every scroll-direction
    // flip via the body-class observer, and most pages have no such element.
    if (!elements.length) {
      return;
    }

    const isScrollingDown = document.body.classList.contains(
      'header-scrolling-down',
    );
    const headerHeight = isScrollingDown
      ? window.getHeaderMainHeight()
      : window.getHeaderHeight();

    // Measure every element up front, then write, so one element's style
    // changes never force a layout for the next one's rect read.
    const measurements = elements.map(measureElement);

    measurements.forEach((measurement) => {
      applyMeasurement(measurement, headerHeight);
    });
  };

  const init = () => {
    applyFixedBelowHeader(document);
    window.refreshWakaFixedBelowHeader = applyFixedBelowHeader;

    let frame = null;
    const refreshOnFrame = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        applyFixedBelowHeader(document);
      });
    };

    window.addEventListener('resize', refreshOnFrame);
    document.addEventListener('waka:morph:done', (event) => {
      applyFixedBelowHeader(event.detail?.root || document);
    });

    const bodyClassObserver = new MutationObserver((mutations) => {
      const hasClassMutation = mutations.some(
        (mutation) =>
          mutation.type === 'attributes' && mutation.attributeName === 'class',
      );

      if (hasClassMutation) {
        refreshOnFrame();
      }
    });

    bodyClassObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

(() => {
  const SELECTOR = '.copy-link-button';
  const COPIED_ATTR = 'data-copied';
  const RESET_DELAY = 2000;

  const handleClick = (event) => {
    const button = event.target.closest(SELECTOR);
    if (!button) return;

    const url = button.dataset.copyUrl;
    if (!url) return;

    navigator.clipboard.writeText(url).then(() => {
      button.setAttribute(COPIED_ATTR, '');
      alert(button.dataset.copiedMessage || 'Link copied!');
      setTimeout(() => {
        button.removeAttribute(COPIED_ATTR);
      }, RESET_DELAY);
    });
  };

  const init = () => {
    document.addEventListener('click', handleClick);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
